// Entry point for the parser layer. Mirrors the CSV branches of
// scripts/loaders.py::detect_portal + load_any. PDF detection (PDF.js) and
// aggregator JSON loaders (Plaid / SimpleFIN) are out of v1 scope — see
// the plan file's "v1 vs deferred" section.

import type { Transaction } from "../common.js";
import { loadBmo } from "./bmo.js";
import { loadCapitalOne } from "./capitalone.js";
import { loadAmex } from "./amex.js";
import { loadChase } from "./chase.js";
import { loadRbc } from "./rbc.js";
import { loadPaypal } from "./paypal.js";
import { loadWiseCsv } from "./wise.js";
import { loadAmazon } from "./amazon.js";
import { loadInstacart } from "./instacart.js";
import { loadEqbankCsv } from "./eqbank.js";
import { loadStripeCsv } from "./stripe.js";
import { loadWealthsimpleCash } from "./wealthsimple.js";
import { loadVenmoCsv } from "./venmo.js";
import { loadGenericCsv, isDateHeaderLine } from "./generic.js";

export type Portal =
  | "bmo"
  | "capitalone"
  | "amex"
  | "chase"
  | "rbc"
  | "paypal"
  | "wise"
  | "amazon"
  | "instacart"
  | "eqbank"
  | "stripe"
  | "wealthsimple_cash"
  | "venmo"
  | "generic_csv"
  | "unknown";

// Sniff the first ~4 KB of the file the way Python does. The order of checks
// matters: more-distinctive markers come first so e.g. a Capital One file
// with "Posted Date" never falls through to the Chase branch.
export function detectPortalFromText(text: string, filename: string): Portal {
  const name = filename.toLowerCase();
  const head = text.slice(0, 4096).toLowerCase();

  // Early filename guards — handle files whose header would otherwise
  // mis-route. `instacart_business_order_summary_sample.csv` has a
  // header with `Order ID` + `Order Date` that trips the Amazon
  // header sniff. Filename hint wins for these.
  if (name.includes("instacart")) return "instacart";

  if (
    (head.includes("first bank card") || head.includes("first bank account")) &&
    head.includes("transaction amount")
  ) {
    return "bmo";
  }
  if (
    (head.includes("posted date") || head.includes("transaction date")) &&
    head.includes("card no") &&
    head.includes("debit") &&
    head.includes("credit")
  ) {
    return "capitalone";
  }
  if (head.includes("card member") && head.includes("account #")) {
    return "amex";
  }
  // PayPal: header always contains Date,Time,TimeZone,Name,Type,Status.
  // The combination of "name,type,status" (with optional quote chars
  // around them) is distinctive enough not to need fuzzier matching.
  const headStripped = head.replace(/["' ]/g, "");
  if (headStripped.includes("name,type,status")) {
    return "paypal";
  }
  if ((head.includes("gross") || head.includes("transaction id")) && head.includes("name") && head.includes("currency")) {
    return "paypal";
  }
  // Wise: legacy header begins "TransferWise ID, Date, Amount, Currency, Description"
  // and modern (2024+) header has "ID, Date, Time, Type, Direction, Source amount".
  // The "source amount" token is rare enough to be a clean signal for modern;
  // "transferwise id" tags the legacy file uniquely.
  if (headStripped.includes("transferwiseid")) return "wise";
  if (headStripped.includes("sourceamount") && headStripped.includes("targetamount")) {
    return "wise";
  }
  // Amazon order history: "Order ID, Order Date, Title, Item Total" is
  // the canonical Items report. Older "Orders" report swaps Item Total →
  // Total Charged. Both have "order id" + "order date" together.
  if (headStripped.includes("orderid") && headStripped.includes("orderdate")) {
    return "amazon";
  }
  // EQ Bank: Date,Description,Out,In,Balance — the Out+In paired columns
  // are distinctive (most banks ship one signed Amount column instead).
  if (head.includes("date") && head.includes("description") && head.includes(",out,") && head.includes(",in,")) {
    return "eqbank";
  }
  // Stripe: id+created/Created (UTC) header. id-first is the giveaway —
  // very few exports lead with a raw `id` column.
  if ((head.startsWith("id,") || head.includes("\nid,")) && head.includes("created")) {
    return "stripe";
  }
  // Wealthsimple Cash: Date,Description,Transaction Type,Amount,Balance.
  // The "transaction type" phrase is distinctive in WS Cash exports.
  if (head.includes("transaction type") && head.includes("amount") && head.includes("balance")) {
    return "wealthsimple_cash";
  }
  // Venmo: has a multi-line preamble; the actual header contains
  // "Datetime,Type,Status,Note,From,To,Amount (total)" — "amount (total)"
  // is the most distinctive single token.
  if (head.includes("amount (total)") && head.includes("datetime")) {
    return "venmo";
  }
  if (head.includes("cad$") && head.includes("usd$")) {
    return "rbc";
  }
  if (
    (head.includes("transaction date") ||
      head.includes("posting date") ||
      head.includes("post date")) &&
    head.includes("description") &&
    head.includes("amount") &&
    !head.includes("debit") &&
    !head.includes("credit") &&
    !head.includes("card no") &&
    !head.includes("first bank") &&
    !head.includes("cad$")
  ) {
    return "chase";
  }

  // Filename hints — when the header sniffer didn't match.
  if (name.includes("bmo")) return "bmo";
  if (name.includes("capitalone") || name.includes("capital-one")) return "capitalone";
  if (name.includes("amex") || name.includes("americanexpress") || name.includes("american-express")) {
    return "amex";
  }
  if (name.includes("chase")) return "chase";
  if (name.includes("rbc") || name.includes("royalbank") || name.includes("royal-bank")) return "rbc";
  if (name.includes("paypal")) return "paypal";
  if (name.includes("wise") || name.includes("transferwise")) return "wise";
  if (name.includes("amazon")) return "amazon";
  if (name.includes("instacart")) return "instacart";
  if (name.includes("eqbank") || name.includes("eq-bank")) return "eqbank";
  if (name.includes("stripe")) return "stripe";
  if (name.includes("wealthsimple") || name.includes("ws-cash") || name.includes("ws_cash")) {
    return "wealthsimple_cash";
  }
  if (name.includes("venmo")) return "venmo";

  // Generic-CSV fallback: any delimited file with a date-y token in one
  // of the first 15 lines gets a best-effort parse. This is what saves
  // unknown banks / fintechs from showing up as "unsupported."
  const headLines = text.slice(0, 4096).split(/\r?\n/).slice(0, 15);
  for (const line of headLines) {
    if (isDateHeaderLine(line)) return "generic_csv";
  }

  return "unknown";
}

// One-call API for the UI layer: given a File-like input, route to the right
// loader and return Transaction[]. Throws on portal === "unknown" so the
// caller can surface a user-friendly message.
export async function loadAny(
  text: string,
  filename: string,
): Promise<{ portal: Portal; transactions: Transaction[] }> {
  const portal = detectPortalFromText(text, filename);
  switch (portal) {
    case "bmo":
      return { portal, transactions: await loadBmo(text, filename) };
    case "capitalone":
      return { portal, transactions: await loadCapitalOne(text, filename) };
    case "amex":
      return { portal, transactions: await loadAmex(text, filename) };
    case "chase":
      return { portal, transactions: await loadChase(text, filename) };
    case "rbc":
      return { portal, transactions: await loadRbc(text, filename) };
    case "paypal":
      return { portal, transactions: await loadPaypal(text, filename) };
    case "wise":
      return { portal, transactions: await loadWiseCsv(text, filename) };
    case "amazon":
      return { portal, transactions: await loadAmazon(text, filename) };
    case "instacart":
      return { portal, transactions: await loadInstacart(text, filename) };
    case "eqbank":
      return { portal, transactions: await loadEqbankCsv(text, filename) };
    case "stripe":
      return { portal, transactions: await loadStripeCsv(text, filename) };
    case "wealthsimple_cash":
      return { portal, transactions: await loadWealthsimpleCash(text, filename) };
    case "venmo":
      return { portal, transactions: await loadVenmoCsv(text, filename) };
    case "generic_csv":
      return { portal, transactions: await loadGenericCsv(text, filename) };
    case "unknown":
      throw new Error(
        `Could not detect a tabular structure in ${filename}. Make sure it's a CSV (or TSV) with a header row containing a date-like column.`,
      );
  }
}
